/* eslint-disable max-lines */
import { useSetAtom } from "jotai";
import type { FunctionComponent, ReactNode } from "react";
import React, { useCallback, useEffect, useRef } from "react";
import ReactPlayer from "react-player";

import { onlyOnePlayingPlayerAtom } from "scmp-app/components/article/article-list/hooks";
import { useArticleSpeechContext } from "scmp-app/components/article/article-speech/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

import { textToSpeechPlayerAtom } from "./atoms";
import { PlaybackRateRadio } from "./playback-rate-radio";
import {
  ButtonGroupContainer,
  CenterButtonGroupContainer,
  CollapsedPlayerContainer,
  CollapsedPlayerLeftContainer,
  CollapsedPlayerPlayIconContainer,
  CollapsedPlayerRightContainer,
  CollapseIconContainer,
  Container,
  ExpandedPlayerContainer,
  ExpandIconContainer,
  FlagIconContainer,
  GrayCircle,
  HeaderContainer,
  LeftContainer,
  PlaybackRateButton,
  PlaybackRateRadioButtonsContainer,
  PlayerContainer,
  PlayIconContainer,
  RemainingTimeContainer,
  RightContainer,
  SeekBar,
  SeekBarContainer,
  StyledBackwardIcon,
  StyledBlackVoiceIcon,
  StyledChevronDownIcon,
  StyledCollapseIcon,
  StyledExpandIcon,
  StyledFormControl,
  StyledFormControlLabel,
  StyledForwardIcon,
  StyledGrayVoiceIcon,
  StyledPauseIcon,
  StyledPlayIcon,
  StyledRadioGroup,
  StyledRestartIcon,
  StyledTickIcon,
  StyledUKFlagIcon,
  StyledUSFlagIcon,
  StyledVoicemailIcon,
  TextContainer,
  ThumbnailContainer,
  ThumbnailImage,
  Time,
  Title,
  TitleBar,
  TitleText,
  VoiceGrid,
  VoiceInfoRow,
  VoiceList,
  VoiceListContainer,
  VoiceName,
  VoiceTextContainer,
} from "./styles";
import { format, getDisplayRemainingMinutesAndSeconds, getWPM } from "./utilities";

const countryInfoMap = {
  "United Kingdom": {
    displayName: "United Kingdom",
    icon: <StyledUKFlagIcon />,
  },
  "United State": {
    displayName: "United States",
    icon: <StyledUSFlagIcon />,
  },
} as Record<string, { displayName: string; icon: ReactNode }>;

/**
 * The component implementation reference:
 * https://github.com/cookpete/react-player/blob/master/examples/react/src/App.tsx
 */
export const TextToSpeechPlayer: FunctionComponent = () => {
  const {
    durationChangeEventQueue,
    setDurationChangeEventQueue,
    setTextToSpeechPlayerState: setState,
    textToSpeechPlayerState: state,
  } = useArticleSpeechContext();

  const {
    collapseStatus,
    duration,
    entityId,
    playbackRate,
    played,
    seeking,
    selectedVoiceIndex,
    shouldShowPlaybackRateRadioButtons,
    shouldShowVoiceList,
    src,
    status,
    thumbnail,
    title,
    voiceNameInfoList,
  } = state;

  const adjustedDuration = duration / playbackRate;

  const playerElement = useRef<HTMLVideoElement | null>(null);
  const triggeredPercentageList = useRef([-1]);

  const setOnlyOnePlayingPlayerAtom = useSetAtom(onlyOnePlayingPlayerAtom);

  const scrollStartReference = useRef<null | number>(null);
  const lastScrollYReference = useRef(0);
  const scrollTimeoutReference = useRef<NodeJS.Timeout | null>(null);

  const hasTriggeredThreeSecondsReference = useRef(false);

  const setTextToSpeechPlayerAtom = useSetAtom(textToSpeechPlayerAtom);

  useEffect(() => {
    const scrollThreshold = 50;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (scrollStartReference.current === null) {
        scrollStartReference.current = currentScrollY;
        lastScrollYReference.current = currentScrollY;
        return;
      }

      const totalScrollDistance = Math.abs(currentScrollY - scrollStartReference.current);
      if (totalScrollDistance >= scrollThreshold) {
        setTextToSpeechPlayerAtom({
          isScrollingOverThreshold: true,
        });
      }

      const currentScrollDistance = Math.abs(currentScrollY - lastScrollYReference.current);
      if (currentScrollDistance < 1) {
        scrollStartReference.current = null;
        setTextToSpeechPlayerAtom({
          isScrollingOverThreshold: false,
        });
      }

      lastScrollYReference.current = currentScrollY;

      if (scrollTimeoutReference.current) {
        clearTimeout(scrollTimeoutReference.current);
      }

      scrollTimeoutReference.current = setTimeout(() => {
        scrollStartReference.current = null;
        setTextToSpeechPlayerAtom({
          isScrollingOverThreshold: false,
        });
        scrollTimeoutReference.current = null;
      }, 200);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);

      if (scrollTimeoutReference.current) {
        clearTimeout(scrollTimeoutReference.current);
      }
    };
  });

  const setPlayerReference = useCallback(
    (player: HTMLVideoElement) => {
      if (!player) return;
      playerElement.current = player;

      setOnlyOnePlayingPlayerAtom(previousState => ({
        ...previousState,
        textToSpeechPlayer: player,
      }));
    },
    [setOnlyOnePlayingPlayerAtom],
  );

  const collapsePlayer = () => {
    setState(previousState => ({
      ...previousState,
      collapseStatus: "collapsed",
    }));
  };

  const expandPlayer = () => {
    setState(previousState => ({
      ...previousState,
      collapseStatus: "expanded-by-user",
    }));
  };

  const handleDurationChange = () => {
    if (!playerElement.current) return;

    const updatedDuration = playerElement.current.duration;
    setState(previousState => ({ ...previousState, duration: updatedDuration }));
    if (durationChangeEventQueue.length > 0) {
      const adjustedDuration = updatedDuration / playbackRate;
      durationChangeEventQueue.forEach(event => event(adjustedDuration));
      setDurationChangeEventQueue([]);
    }
  };

  const handleEnded = () => {
    setState(previousState => ({ ...previousState, status: "ended" }));

    sendGA4Tracking({
      action: "sys",
      category: "tts",
      customized_parameters: {
        action_type: "finished",
        article_id: entityId,
        audio_length: Math.ceil(adjustedDuration),
        progress_fraction: 1,
      },
      subcategory: "",
    });

    triggeredPercentageList.current = [-1];
  };

  /**
   * This event handler is required to update the status from `playing` to `paused`
   * because the `playing` property of the ReactPlayer can be updated.
   * Calling `latestTextToSpeechPlayer.current?.pause()` in `useOnlyOnePlayingPlayer` hook
   * could make the text-to-speech player pause
   * rather than triggering the `play` event listener defined in `useOnlyOnePlayingPlayer` hook
   */
  const handlePause = () => {
    setState(previousState => ({ ...previousState, status: "paused" }));
  };

  const handlePlay = () => {
    setState(previousState => ({ ...previousState, status: "playing" }));

    if (adjustedDuration === 0) {
      setDurationChangeEventQueue(previousQueue => [
        ...previousQueue,
        updatedDuration => {
          sendGA4Tracking({
            action: "sys",
            category: "tts",
            customized_parameters: {
              action_type: "play",
              article_id: entityId,
              audio_length: Math.ceil(updatedDuration),
              progress_fraction: played,
            },
            subcategory: "",
          });
        },
      ]);
      return;
    }

    sendGA4Tracking({
      action: "sys",
      category: "tts",
      customized_parameters: {
        action_type: "play",
        article_id: entityId,
        audio_length: Math.ceil(adjustedDuration),
        progress_fraction: played,
      },
      subcategory: "",
    });
  };

  const handlePlayIconClick = () => {
    if (playerElement.current && status === "ended") {
      playerElement.current.currentTime = 0;

      setState(previousState => ({
        ...previousState,
        played: 0,
        status: "playing",
      }));

      sendGA4Tracking({
        action: "click",
        category: "tts",
        customized_parameters: {
          action_type: "resume",
          article_id: entityId,
          audio_length: Math.ceil(adjustedDuration),
          progress_fraction: played,
        },
        subcategory: "",
      });

      return;
    }

    setState(previousState => {
      const updatedStatus = previousState.status === "playing" ? "paused" : "playing";

      const actionType = previousState.status === "playing" ? "pause" : "resume";

      sendGA4Tracking({
        action: "click",
        category: "tts",
        customized_parameters: {
          action_type: actionType,
          article_id: entityId,
          audio_length: Math.ceil(adjustedDuration),
          progress_fraction: played,
        },
        subcategory: "",
      });

      if (actionType === "pause") {
        /**
         * The player will trigger pause event before ended.
         * Move the pause tracking here instead of putting in the `onPause` handler
         * to avoid triggering unnecessary pause event when ended
         */
        sendGA4Tracking({
          action: "sys",
          category: "tts",
          customized_parameters: {
            action_type: "pause",
            article_id: entityId,
            audio_length: Math.ceil(adjustedDuration),
            progress_fraction: played,
          },
          subcategory: "",
        });
      }

      return {
        ...previousState,
        status: updatedStatus,
      };
    });
  };

  const handlePlaybackRateChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const inputTarget = event.target as HTMLInputElement;
    const updatedPlaybackRate = Number.parseFloat(inputTarget.value);

    setState(previousState => ({
      ...previousState,
      playbackRate: updatedPlaybackRate,
      shouldShowPlaybackRateRadioButtons: false,
    }));

    sendGA4Tracking({
      action: "click",
      category: "tts",
      customized_parameters: {
        article_id: entityId,
        progress_fraction: played,
        speed: getWPM(updatedPlaybackRate),
      },
      subcategory: "speedchanged",
    });
  };

  const handleSeekChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const inputTarget = event.target as HTMLInputElement;
    /**
     * Ensure that played is always between 0 and 1 even if currentTime exceeds duration
     * to prevent the seek bar from overflowing
     */
    const played = Math.min(Number.parseFloat(inputTarget.value), 1);
    setState(previousState => ({
      ...previousState,
      played,
    }));
  };

  const handleSeekingEnd = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const inputTarget = event.target as HTMLInputElement;
    /**
     * Ensure that played is always between 0 and 1 even if currentTime exceeds duration
     * to prevent the seek bar from overflowing
     */
    const played = Math.min(Number.parseFloat(inputTarget.value), 1);
    /**
     * The playerElement's duration is not affected by playback rate
     * so we need to calculate the currentTime based on the original duration
     */
    const updatedCurrentTime = played * duration;
    if (playerElement.current) {
      playerElement.current.currentTime = updatedCurrentTime;
    }
    if (played === 1) {
      setState(previousState => ({ ...previousState, seeking: false, status: "ended" }));
      handleEnded();
    } else {
      setState(previousState => ({ ...previousState, seeking: false }));
    }
  };

  const handleSeekingStart = () => {
    setState(previousState => ({ ...previousState, seeking: true }));
  };

  const handleTimeUpdate = () => {
    // Update the time slider if not seeking or ended
    if (!playerElement.current || seeking || status === "ended") return;

    const updatedPlayedSeconds = playerElement.current.currentTime;

    /**
     * Ensure that played is always between 0 and 1 even if currentTime exceeds duration
     * to prevent the seek bar from overflowing
     */
    const updatedPlayed = Math.min(updatedPlayedSeconds / duration, 1);

    setState(previousState => ({
      ...previousState,
      played: updatedPlayed,
    }));

    const percentageElapsed = Math.round(updatedPlayed * 100);

    if (percentageElapsed > 0 && percentageElapsed % 10 === 0) {
      const alreadySent = triggeredPercentageList.current.includes(percentageElapsed);

      if (!alreadySent) {
        triggeredPercentageList.current.push(percentageElapsed);
        sendGA4Tracking({
          action: "sys",
          category: "tts",
          customized_parameters: {
            action_type: "progress",
            article_id: entityId,
            audio_length: Math.ceil(adjustedDuration),
            progress_fraction: updatedPlayed,
          },
          subcategory: "",
        });
      }
    }

    if (
      !hasTriggeredThreeSecondsReference.current &&
      adjustedDuration &&
      updatedPlayedSeconds >= 3
    ) {
      hasTriggeredThreeSecondsReference.current = true;

      sendGA4Tracking({
        action: "sys",
        category: "tts",
        customized_parameters: {
          action_type: "3s",
          article_id: entityId,
          audio_length: Math.ceil(adjustedDuration),
          progress_fraction: updatedPlayedSeconds / adjustedDuration,
        },
        subcategory: "",
      });
    }
  };

  const handleBackwardButtonClick = () => {
    if (!playerElement.current) return;

    setState(previousState => {
      const previousPlayedSeconds = previousState.played * previousState.duration;
      const decreasedPlayedSeconds = 5 / previousState.playbackRate;
      const updatedPlayedSeconds =
        previousPlayedSeconds >= decreasedPlayedSeconds
          ? previousPlayedSeconds - decreasedPlayedSeconds
          : previousPlayedSeconds;
      playerElement.current!.currentTime = updatedPlayedSeconds;

      return {
        ...previousState,
        played: Math.min(updatedPlayedSeconds / previousState.duration, 1),
      };
    });
  };

  const handleForwardButtonClick = () => {
    if (!playerElement.current) return;

    setState(previousState => {
      const previousPlayedSeconds = previousState.played * previousState.duration;
      const increasedPlayedSeconds = 5 / previousState.playbackRate;
      const updatedPlayedSeconds =
        previousPlayedSeconds + increasedPlayedSeconds <= previousState.duration
          ? previousPlayedSeconds + increasedPlayedSeconds
          : previousPlayedSeconds;
      playerElement.current!.currentTime = updatedPlayedSeconds;

      return {
        ...previousState,
        played: Math.min(updatedPlayedSeconds / previousState.duration, 1),
      };
    });
  };

  const getVoiceChangeHandler = (index: number) => () => {
    hasTriggeredThreeSecondsReference.current = false;
    setState(previousState => ({
      ...previousState,
      hasTriggeredThreeSeconds: false,
    }));

    setState(previousState => ({
      ...previousState,
      played: 0,
      selectedVoiceIndex: index,
      src: voiceNameInfoList[index].url,
    }));

    sendGA4Tracking({
      action: "click",
      category: "tts",
      customized_parameters: {
        article_id: entityId,
        progress_fraction: played,
        voice: voiceNameInfoList[index].voiceName,
      },
      subcategory: "voicechanged",
    });
  };

  const collapseVoiceList = () => {
    setState(previousState => ({
      ...previousState,
      shouldShowVoiceList: false,
    }));
  };

  const toggleShouldShowVoiceList = () => {
    setState(previousState => {
      const updatedShouldShowVoiceList = !previousState.shouldShowVoiceList;

      const updatedShouldShowPlaybackRateSlider = updatedShouldShowVoiceList
        ? false
        : previousState.shouldShowPlaybackRateRadioButtons;

      return {
        ...previousState,
        shouldShowPlaybackRateRadioButtons: updatedShouldShowPlaybackRateSlider,
        shouldShowVoiceList: updatedShouldShowVoiceList,
      };
    });
  };

  const collapsePlaybackRateSlider = () => {
    setState(previousState => ({
      ...previousState,
      shouldShowPlaybackRateRadioButtons: false,
    }));
  };

  const toggleShouldShowPlaybackRateSlider = () => {
    setState(previousState => {
      const updatedShouldShowPlaybackRateSlider = !previousState.shouldShowPlaybackRateRadioButtons;

      const updatedShouldShowVoiceList = updatedShouldShowPlaybackRateSlider
        ? false
        : previousState.shouldShowVoiceList;

      return {
        ...previousState,
        shouldShowPlaybackRateRadioButtons: updatedShouldShowPlaybackRateSlider,
        shouldShowVoiceList: updatedShouldShowVoiceList,
      };
    });
  };

  const isShowingHeader = !shouldShowPlaybackRateRadioButtons && !shouldShowVoiceList;

  const renderPlayIcon = () => {
    switch (status) {
      case "ended":
        return <StyledRestartIcon />;
      case "hidden":
        return null;
      case "paused":
        return <StyledPlayIcon />;
      case "playing":
        return <StyledPauseIcon />;
    }
  };

  return (
    <Container $isAudioLoaded={!!src && status !== "hidden"}>
      <ReactPlayer
        onDurationChange={handleDurationChange}
        onEnded={handleEnded}
        onPause={handlePause}
        onPlay={handlePlay}
        onTimeUpdate={handleTimeUpdate}
        playbackRate={playbackRate}
        playing={status === "playing"}
        ref={setPlayerReference}
        src={src}
      />
      {collapseStatus === "collapsed" ? (
        <CollapsedPlayerContainer>
          <CollapsedPlayerLeftContainer>
            <CollapsedPlayerPlayIconContainer onClick={handlePlayIconClick}>
              {renderPlayIcon()}
            </CollapsedPlayerPlayIconContainer>
            <RemainingTimeContainer>
              {getDisplayRemainingMinutesAndSeconds(adjustedDuration * (1 - played))}
            </RemainingTimeContainer>
          </CollapsedPlayerLeftContainer>
          <CollapsedPlayerRightContainer>
            <ExpandIconContainer onClick={expandPlayer}>
              <StyledExpandIcon />
            </ExpandIconContainer>
          </CollapsedPlayerRightContainer>
        </CollapsedPlayerContainer>
      ) : (
        <ExpandedPlayerContainer $isShowingHeader={isShowingHeader}>
          <HeaderContainer $isShowingHeader={isShowingHeader}>
            <ThumbnailContainer>
              {thumbnail && <ThumbnailImage src={thumbnail} />}
            </ThumbnailContainer>
            <Title>{title}</Title>
            <CollapseIconContainer onClick={collapsePlayer}>
              <StyledCollapseIcon />
            </CollapseIconContainer>
          </HeaderContainer>
          <VoiceListContainer $shouldShowVoiceList={shouldShowVoiceList}>
            <div>
              <TitleBar>
                <StyledBlackVoiceIcon />
                <TitleText>Select Voice</TitleText>
                <StyledChevronDownIcon onClick={collapseVoiceList} />
              </TitleBar>
              <VoiceList>
                {voiceNameInfoList.map((info, index) => (
                  <VoiceGrid key={info.voiceName} onClick={getVoiceChangeHandler(index)}>
                    <LeftContainer>
                      <FlagIconContainer>{countryInfoMap[info.country].icon}</FlagIconContainer>
                      <VoiceTextContainer>
                        <VoiceName>{info.voiceName}</VoiceName>
                        <VoiceInfoRow>
                          {info.gender}・{countryInfoMap[info.country].displayName}
                        </VoiceInfoRow>
                      </VoiceTextContainer>
                    </LeftContainer>
                    <RightContainer>
                      {selectedVoiceIndex === index ? <StyledTickIcon /> : <GrayCircle />}
                    </RightContainer>
                  </VoiceGrid>
                ))}
              </VoiceList>
            </div>
          </VoiceListContainer>
          <PlaybackRateRadioButtonsContainer
            $shouldShowPlaybackRateRadioButtons={shouldShowPlaybackRateRadioButtons}
          >
            <div>
              <TitleBar>
                <StyledVoicemailIcon />
                <TextContainer>
                  <TitleText>Select Speed</TitleText>
                </TextContainer>
                <StyledChevronDownIcon onClick={collapsePlaybackRateSlider} />
              </TitleBar>
              <StyledFormControl>
                <StyledRadioGroup
                  aria-labelledby="playback-rate-radio-buttons-group-label"
                  name="playback-rate-radio-buttons-group"
                  onChange={handlePlaybackRateChange}
                  value={playbackRate}
                >
                  {["0.8", "0.9", "1.0", "1.1", "1.2", "1.5", "1.75"].map(value => (
                    <StyledFormControlLabel
                      control={<PlaybackRateRadio />}
                      key={`playback-rate-radio-button-${value}`}
                      label={`${value}x`}
                      labelPlacement="start"
                      value={Number.parseFloat(value)}
                    />
                  ))}
                </StyledRadioGroup>
              </StyledFormControl>
            </div>
          </PlaybackRateRadioButtonsContainer>
          <PlayerContainer>
            <SeekBarContainer>
              <Time>{format(played * adjustedDuration)}</Time>
              <SeekBar
                $played={played}
                max={1}
                min={0}
                onChange={handleSeekChange}
                onMouseDown={handleSeekingStart}
                onMouseUp={handleSeekingEnd}
                onTouchEnd={handleSeekingEnd}
                onTouchStart={handleSeekingStart}
                step="any"
                type="range"
                value={played}
              />
              <Time>{format(adjustedDuration)}</Time>
            </SeekBarContainer>
            <ButtonGroupContainer>
              <StyledGrayVoiceIcon onClick={toggleShouldShowVoiceList} />
              <CenterButtonGroupContainer>
                <StyledBackwardIcon onClick={handleBackwardButtonClick} />
                <PlayIconContainer onClick={handlePlayIconClick}>
                  {renderPlayIcon()}
                </PlayIconContainer>
                <StyledForwardIcon onClick={handleForwardButtonClick} />
              </CenterButtonGroupContainer>
              <PlaybackRateButton onClick={toggleShouldShowPlaybackRateSlider}>
                {playbackRate.toFixed(2)}x
              </PlaybackRateButton>
            </ButtonGroupContainer>
          </PlayerContainer>
        </ExpandedPlayerContainer>
      )}
    </Container>
  );
};

TextToSpeechPlayer.displayName = "TextToSpeechPlayer";
